import { statusTag } from '@/features/common/statusTag';
import type { StatusTagVariant } from '@/features/common/types/StatusTagVariant';
import type { MeasureStatus } from '@/features/publicsite/types/funding/MeasureStatus';
import type { PublicCatalogKey } from '@/i18n/dictionaries/publicCatalog';

/**
 * The two things a funding card decides about a measure's status, kept out of the component so
 * they are testable — this project's Vitest setup has no DOM and cannot render one.
 *
 * funding_measure.status is CHECKed to four values in the database: UPCOMING, OPEN, CLOSED,
 * SUSPENDED.
 */

/** The four, as the code names them — the union is MeasureStatus. */
export const MEASURE_STATUS = {
  upcoming: 'UPCOMING',
  open: 'OPEN',
  closed: 'CLOSED',
  suspended: 'SUSPENDED',
} as const;

/**
 * Whether a status is one of the four. It travels as free text, so the two functions below
 * upper-case it first and narrow it here — a value outside the four gets their fallback, never a
 * cast.
 */
export function isMeasureStatus(code: string): code is MeasureStatus {
  return Object.values<string>(MEASURE_STATUS).includes(code);
}

const STATUS_KEY: Record<MeasureStatus, PublicCatalogKey> = {
  OPEN: 'fundingStatusOpen',
  UPCOMING: 'fundingStatusUpcoming',
  CLOSED: 'fundingStatusClosed',
  SUSPENDED: 'fundingStatusSuspended',
};

/** Dictionary key for the badge's text. The component translates; this stays language-agnostic. */
export function measureStatusKey(status: string): PublicCatalogKey {
  const code = status.toUpperCase();
  return isMeasureStatus(code) ? STATUS_KEY[code] : 'fundingStatusUnknown';
}

const STATUS_VARIANT: Record<MeasureStatus, StatusTagVariant> = {
  OPEN: 'info',
  SUSPENDED: 'accent',
  CLOSED: 'neutral',
  UPCOMING: 'accent',
};

/**
 * The tag's colour, as one of MUD's `.status-tag` variants — the one pill every status in the app
 * is drawn as.
 *
 * Not statusTagClass() from features/common/statusTag.ts, though it takes the same kind of input:
 * the rules behind it are written for dossier workflows and read this vocabulary wrong. CLOSED
 * matches none of its patterns and would fall through to the blue that says "open" on the card
 * beside it, and SUSPENDED lands in its danger bucket, red, which is a verdict about a person's
 * dossier and not what a paused call for applications is. A catalogue of funding calls is a
 * different vocabulary, so it maps its own four words — to the same four variants, named here as
 * MUD names them rather than as tones, since no tone is being classified.
 */
export function measureStatusClass(status: string): string {
  const code = status.toUpperCase();
  return statusTag(isMeasureStatus(code) ? STATUS_VARIANT[code] : 'neutral');
}
